import React, { useEffect, useCallback } from "react";
import { ExclamationCircleFilled, IdcardOutlined, SwapOutlined } from "@ant-design/icons";
import { Col, List, Row, Tag, Checkbox } from "antd";
import { TrailerTypeMap, TrailerType } from "@/utils/enum";
import { checkHasScrolledToBottomOfElement, getGuidFirst8 } from "@/utils/utils";
import TWIC from "@/components/Tags/TWIC";
import ShipmentRequirementsSummaryItem from "./ShipmentRequirementsSummaryItem";

export const renderShipmentTagsList = (shipmentData, returnArray = false) => {
  const tags = [];
  const {
    childShipmentId,
    parentShipmentId,
    requiresEModal,
    requiresTWIC,
    requiresLotExit,
    trailerType,
    isExchangeRequired,
  } = shipmentData;

  tags.push(
    <Tag
      key={1}
      color={trailerType === TrailerType.Enclosed ? "purple" : "blue"}
      style={{ marginLeft: 5, fontWeight: "bold" }}
    >
      {TrailerTypeMap[trailerType]}
    </Tag>,
  );

  if (requiresEModal) {
    tags.push(
      <Tag key={0} color="purple" style={{ marginLeft: 5, fontWeight: "bold" }} icon={<IdcardOutlined />}>
        EMODAL
      </Tag>,
    );
  }

  if (requiresTWIC) {
    tags.push(<TWIC key={1} />);
  }

  if (requiresLotExit) {
    tags.push(
      <Tag key={2} color="orange" style={{ marginLeft: 5, fontWeight: "bold" }} icon={<ExclamationCircleFilled />}>
        Lot Exit
      </Tag>,
    );
  }

  if (childShipmentId || parentShipmentId) {
    const text = isExchangeRequired ? "Dealer Exchange (required)" : "Dealer Exchange";
    const color = isExchangeRequired ? "warning" : "processing";

    tags.push(
      <Tag key={3} color={color} style={{ marginLeft: 5, fontWeight: "bold" }} icon={<SwapOutlined />}>
        {text}
      </Tag>,
    );
  }

  return returnArray ? tags : <Row justify="end">{tags}</Row>;
};

const renderItemTitle = shipmentData => {
  const { id } = shipmentData;

  return (
    <Row justify="space-between">
      <Col flex={1}>
        <span style={{ fontSize: 18, fontWeight: "bold" }}>{`Shipment - ${getGuidFirst8(id)}`}</span>
      </Col>
      <Col flex={1}>{renderShipmentTagsList(shipmentData)}</Col>
    </Row>
  );
};

const handleListScroll = (e, setHasScrolledToBottomOfRequirements) => {
  if (!setHasScrolledToBottomOfRequirements) return;
  const element = e.target;
  const scrolledToBottom = checkHasScrolledToBottomOfElement(element);

  if (scrolledToBottom) {
    setHasScrolledToBottomOfRequirements(true);
  }
};

const ShipmentRequirementsSummary = props => {
  const { shipmentsList, setHasScrolledToBottomOfRequirements, hasCheckedAllVehicles, setHasCheckedAllVehicles } =
    props;

  const handleCheckedAllVehiclesChange = useCallback(
    event => {
      setHasCheckedAllVehicles(!hasCheckedAllVehicles);
      event?.preventDefault?.();
    },
    [hasCheckedAllVehicles, setHasCheckedAllVehicles],
  );

  useEffect(() => {
    if (!setHasScrolledToBottomOfRequirements) return;

    // Check if we need to require user to scroll through summary again to ensure they view requirements for the newly selected shipments
    const element = document.querySelector(".shipment-requirements-list");
    const scrolledToBottom = checkHasScrolledToBottomOfElement(element);

    if (scrolledToBottom) {
      setHasScrolledToBottomOfRequirements(true);
      return;
    }

    setHasScrolledToBottomOfRequirements(false);
  });

  return (
    <>
      <List
        className="shipment-requirements-list"
        bordered
        dataSource={[...shipmentsList, "checkAllVehicles"]}
        rowKey={item => (item === "checkAllVehicles" ? item : item.id)}
        style={{ maxHeight: 520, overflowY: "auto" }}
        onScroll={e => handleListScroll(e, setHasScrolledToBottomOfRequirements)}
        renderItem={item =>
          item === "checkAllVehicles" ? (
            <div
              style={{
                paddingLeft: "28px",
                paddingBottom: "28px",
                paddingTop: "12px",
                marginTop: "12px",
              }}
              onClick={handleCheckedAllVehiclesChange}
            >
              <Checkbox checked={hasCheckedAllVehicles} onChange={handleCheckedAllVehiclesChange}>
                <span onClick={handleCheckedAllVehiclesChange}>
                  I confirm that I meet the requirements to transport the selected units, and I have read all pickup and
                  dropoff instructions.
                </span>
              </Checkbox>
            </div>
          ) : (
            <List.Item style={{ marginTop: 8, marginBottom: 8 }}>
              <List.Item.Meta
                title={renderItemTitle(item)}
                description={<ShipmentRequirementsSummaryItem shipmentData={item} />}
              />
            </List.Item>
          )
        }
      />
    </>
  );
};

export default ShipmentRequirementsSummary;
